import { useMemo } from 'react'
import { Column, Cell } from 'react-table'
import { Button, ButtonProps, HStack } from '@ritualco/jutsu'
import { BasicTable } from 'components/BasicTable'
import { useContent } from 'lib/content/react'
import { WorkspaceMember } from 'types/entities'

const AddButton = (props: ButtonProps) => (
  <Button size="sm" width={24} {...props} />
)

const RemoveButton = (props: ButtonProps) => (
  <Button size="sm" variant="outline" width={24} {...props} />
)

export type IncentivePeopleTableProps = {
  /** List of members that can be added/removed */
  workspaceMembers?: WorkspaceMember[]
  /** IDs of currently added members */
  people: string[]
  /** Message displayed when there are no workspace members */
  placeholder: string
  isDisabled?: boolean
  onAdd: (value: string) => void
  onRemove: (value: string) => void
}

export const IncentivePeopleTable = (props: IncentivePeopleTableProps) => {
  const {
    workspaceMembers = [],
    people,
    placeholder,
    isDisabled = false,
    onAdd,
    onRemove,
  } = props
  const { formatMessage } = useContent()
  const columns = useMemo<Column<WorkspaceMember>[]>(
    () => [
      {
        accessor: 'email',
        Header: formatMessage({ id: 'pmportal.incentives.person' }),
      },
      {
        id: 'actions',
        accessor: row => people.some(id => id === row.id),
        Cell: ({ row, value }: Cell<WorkspaceMember>) => (
          <HStack spacing={2} justifyContent="flex-end">
            {value ? (
              <RemoveButton
                isDisabled={isDisabled}
                onClick={() => onRemove(row.original.id)}
              >
                {formatMessage({ id: 'pmportal.global.actionRemove' })}
              </RemoveButton>
            ) : (
              <AddButton
                isDisabled={isDisabled}
                onClick={() => onAdd(row.original.id)}
              >
                {formatMessage({ id: 'pmportal.global.actionAdd' })}
              </AddButton>
            )}
          </HStack>
        ),
      },
    ],
    [formatMessage, people, isDisabled, onAdd, onRemove]
  )

  return (
    <BasicTable
      columns={columns}
      data={workspaceMembers}
      placeholder={placeholder}
      tdProps={() => ({
        color: isDisabled ? 'grayscale.tertiary' : 'grayscale.primary',
      })}
    />
  )
}
